import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import styled from 'styled-components';
import {
  frameRect,
  LAYOUT_GRID_MM,
  snapMm,
  stampRect,
  ZONE_KEYS,
  ZONE_LABELS,
  ZONE_MIN,
  zoneRect,
  type GeoRect,
  type GeoZoneKey,
  type GeoZones,
  type LayoutProblem,
  type SheetSize,
} from '@entities/geodesy';

type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface DragState {
  key: GeoZoneKey;
  mode: 'move' | Handle;
  /** Точка захвата, мм листа (y вниз — как в SVG). */
  startX: number;
  startY: number;
  start: GeoRect;
}

const HANDLES: ReadonlyArray<Handle> = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

const Wrap = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(3)};
`;

const Svg = styled.svg<{ $editable: boolean }>`
  display: block;
  width: 100%;
  height: auto;
  touch-action: ${({ $editable }) => ($editable ? 'none' : 'auto')};
  user-select: none;

  .paper {
    fill: ${({ theme }) => theme.colors.backgroundElevated};
    stroke: ${({ theme }) => theme.colors.border};
    vector-effect: non-scaling-stroke;
  }
  .grid {
    stroke: ${({ theme }) => theme.colors.border};
    stroke-width: 0.5;
    vector-effect: non-scaling-stroke;
  }
  .grid-major {
    stroke: ${({ theme }) => theme.colors.borderStrong};
    stroke-width: 0.6;
    vector-effect: non-scaling-stroke;
  }
  .frame {
    fill: none;
    stroke: ${({ theme }) => theme.colors.text.primary};
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }
  .stamp {
    fill: ${({ theme }) => theme.colors.surface};
    stroke: ${({ theme }) => theme.colors.text.primary};
    stroke-width: 1.25;
    vector-effect: non-scaling-stroke;
  }
  .stamp.conflict {
    stroke: ${({ theme }) => theme.colors.status.danger};
    stroke-width: 2.5;
  }
  .stamp-line {
    stroke: ${({ theme }) => theme.colors.text.secondary};
    stroke-width: 0.6;
    vector-effect: non-scaling-stroke;
  }
  .stamp-text {
    fill: ${({ theme }) => theme.colors.text.secondary};
    font-family: ${({ theme }) => theme.font.family};
  }
  .overlap {
    fill: ${({ theme }) => theme.colors.status.danger}55;
    stroke: ${({ theme }) => theme.colors.status.danger};
    stroke-dasharray: 3 2;
    vector-effect: non-scaling-stroke;
    pointer-events: none;
  }
`;

const Zone = styled.g<{
  $selected: boolean;
  $conflict: boolean;
  $main: boolean;
  $editable: boolean;
}>`
  cursor: ${({ $editable }) => ($editable ? 'move' : 'pointer')};
  outline: none;

  .body {
    fill: ${({ theme, $main, $conflict }) =>
      $conflict
        ? `${theme.colors.status.danger}1F`
        : $main
          ? theme.colors.primarySoft
          : `${theme.colors.status.info}14`};
    stroke: ${({ theme, $selected, $conflict }) =>
      $conflict
        ? theme.colors.status.danger
        : $selected
          ? theme.colors.primary
          : theme.colors.borderStrong};
    stroke-width: ${({ $selected }) => ($selected ? 2.25 : 1.25)};
    stroke-dasharray: ${({ $main }) => ($main ? 'none' : '5 3')};
    vector-effect: non-scaling-stroke;
    transition: stroke ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};
  }
  .title {
    fill: ${({ theme, $conflict }) => ($conflict ? theme.colors.status.danger : theme.colors.text.primary)};
    font-family: ${({ theme }) => theme.font.family};
    font-weight: 600;
    pointer-events: none;
  }
  .size {
    fill: ${({ theme }) => theme.colors.text.secondary};
    font-family: ${({ theme }) => theme.font.mono};
    pointer-events: none;
  }
  .handle {
    fill: ${({ theme }) => theme.colors.backgroundElevated};
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 1.5;
    vector-effect: non-scaling-stroke;
  }
  .focus {
    fill: none;
    stroke: ${({ theme }) => theme.colors.primary};
    stroke-width: 2;
    stroke-dasharray: 4 3;
    vector-effect: non-scaling-stroke;
    visibility: hidden;
    pointer-events: none;
  }
  &:focus-visible .focus {
    visibility: visible;
  }
`;

interface Props {
  size: SheetSize;
  zones: GeoZones;
  problems: LayoutProblem[];
  selected: GeoZoneKey | null;
  editable: boolean;
  onSelect: (key: GeoZoneKey) => void;
  onChange: (key: GeoZoneKey, rect: GeoRect) => void;
}

/** Пересечение двух прямоугольников (для штриховки конфликта) или null. */
function intersection(a: GeoRect, b: GeoRect): GeoRect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.w, b.x + b.w);
  const y2 = Math.min(a.y + a.h, b.y + b.h);
  return x2 > x && y2 > y ? { x, y, w: x2 - x, h: y2 - y } : null;
}

/**
 * Схема листа в масштабе: рамка (поле подшивки 20 мм, прочие 5 мм), штамп формы 3
 * 185×55 мм — неподвижен; зоны двигаются и тянутся за края мышью или пальцем
 * с привязкой к сетке 5 мм. С клавиатуры: стрелки — сдвиг на 5 мм, Shift+стрелки —
 * размер. Координаты зон — мм от левого нижнего угла листа (как в контракте).
 */
export function LayoutEditor({
  size,
  zones,
  problems,
  selected,
  editable,
  onSelect,
  onChange,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<DragState | null>(null);
  // id для clipPath: подписи не вылезают за свою область.
  const clipBase = `geo-zone-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [pxPerMm, setPxPerMm] = useState(1);

  const W = size.w;
  const H = size.h;
  const frame = frameRect(size);
  const stamp = stampRect(size);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0) setPxPerMm(entry.contentRect.width / W);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [W]);

  /** Экранные пиксели → мм листа. */
  const mm = (px: number) => px / pxPerMm;
  const conflictKeys = new Set(problems.flatMap((p) => p.zones));
  const stampConflict = problems.some((p) => p.stamp);

  function pointer(e: PointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const r = svg.getBoundingClientRect();
    if (r.width === 0) return null;
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  }

  function clampRect(r: GeoRect): GeoRect {
    const w = Math.min(Math.max(r.w, ZONE_MIN.w), frame.w);
    const h = Math.min(Math.max(r.h, ZONE_MIN.h), frame.h);
    const x = Math.min(Math.max(r.x, frame.x), frame.x + frame.w - w);
    const y = Math.min(Math.max(r.y, frame.y), frame.y + frame.h - h);
    return { x, y, w, h };
  }

  function begin(e: PointerEvent<SVGElement>, key: GeoZoneKey, mode: DragState['mode']) {
    e.stopPropagation();
    onSelect(key);
    if (!editable) return;
    const p = pointer(e);
    const start = zoneRect(zones, key);
    if (!p || !start) return;
    svgRef.current?.setPointerCapture(e.pointerId);
    drag.current = { key, mode, startX: p.x, startY: p.y, start };
  }

  function move(e: PointerEvent<SVGSVGElement>) {
    const d = drag.current;
    if (!d) return;
    const p = pointer(e);
    if (!p) return;
    const dx = p.x - d.startX;
    const dyUp = -(p.y - d.startY); // в мм листа ось y вверх
    const s = d.start;
    let next: GeoRect;
    if (d.mode === 'move') {
      next = { x: snapMm(s.x + dx), y: snapMm(s.y + dyUp), w: s.w, h: s.h };
    } else {
      let left = s.x;
      let right = s.x + s.w;
      let bottom = s.y;
      let top = s.y + s.h;
      if (d.mode.includes('w')) left = Math.min(snapMm(s.x + dx), right - ZONE_MIN.w);
      if (d.mode.includes('e')) right = Math.max(snapMm(s.x + s.w + dx), left + ZONE_MIN.w);
      if (d.mode.includes('s')) bottom = Math.min(snapMm(s.y + dyUp), top - ZONE_MIN.h);
      if (d.mode.includes('n')) top = Math.max(snapMm(s.y + s.h + dyUp), bottom + ZONE_MIN.h);
      left = Math.max(left, frame.x);
      bottom = Math.max(bottom, frame.y);
      right = Math.min(right, frame.x + frame.w);
      top = Math.min(top, frame.y + frame.h);
      next = { x: left, y: bottom, w: right - left, h: top - bottom };
    }
    const clamped = d.mode === 'move' ? clampRect(next) : next;
    const cur = zoneRect(zones, d.key);
    if (
      !cur ||
      cur.x !== clamped.x ||
      cur.y !== clamped.y ||
      cur.w !== clamped.w ||
      cur.h !== clamped.h
    ) {
      onChange(d.key, clamped);
    }
  }

  function end(e: PointerEvent<SVGSVGElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    drag.current = null;
  }

  function keyboard(e: KeyboardEvent<SVGGElement>, key: GeoZoneKey) {
    const r = zoneRect(zones, key);
    if (!r) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(key);
      return;
    }
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, 1],
      ArrowDown: [0, -1],
    };
    const dir = arrows[e.key];
    if (!dir) return;
    e.preventDefault();
    onSelect(key);
    if (!editable) return;
    const step = LAYOUT_GRID_MM;
    if (e.shiftKey) {
      onChange(key, clampRect({ ...r, w: r.w + dir[0] * step, h: r.h + dir[1] * step }));
    } else {
      onChange(key, clampRect({ ...r, x: r.x + dir[0] * step, y: r.y + dir[1] * step }));
    }
  }

  // Сетка: шаг 5 мм, если он крупнее 6 px, иначе 10 мм; каждые 50 мм — жирнее.
  const minor = pxPerMm * 5 >= 6 ? 5 : 10;
  const gridLines: { x1: number; y1: number; x2: number; y2: number; major: boolean }[] = [];
  for (let x = 0; x <= W; x += minor)
    gridLines.push({ x1: x, y1: 0, x2: x, y2: H, major: x % 50 === 0 });
  for (let y = 0; y <= H; y += minor)
    gridLines.push({ x1: 0, y1: H - y, x2: W, y2: H - y, major: y % 50 === 0 });

  const titleSize = mm(12.5);
  const smallSize = mm(11);
  const handle = mm(10);

  // Штриховка пересечений.
  const present = ZONE_KEYS.flatMap((k) => {
    const r = zoneRect(zones, k);
    return r ? [{ k, r }] : [];
  });
  const overlaps: GeoRect[] = [];
  for (let i = 0; i < present.length; i += 1) {
    const a = present[i];
    if (!a) continue;
    const withStamp = intersection(a.r, stamp);
    if (withStamp) overlaps.push(withStamp);
    for (let j = i + 1; j < present.length; j += 1) {
      const b = present[j];
      const x = b ? intersection(a.r, b.r) : null;
      if (x) overlaps.push(x);
    }
  }

  return (
    <Wrap>
      <Svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        $editable={editable}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        role="group"
        aria-label={`Лист ${W}×${H} мм: рамка, основная надпись и области`}
      >
        <rect className="paper" x={0} y={0} width={W} height={H} />
        {gridLines.map((l, i) => (
          <line
            key={i}
            className={l.major ? 'grid-major' : 'grid'}
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
          />
        ))}
        <rect
          className="frame"
          x={frame.x}
          y={H - frame.y - frame.h}
          width={frame.w}
          height={frame.h}
        />

        <g aria-label="Основная надпись, форма 3, 185 на 55 мм — неподвижна">
          <rect
            className={stampConflict ? 'stamp conflict' : 'stamp'}
            x={stamp.x}
            y={H - stamp.y - stamp.h}
            width={stamp.w}
            height={stamp.h}
          />
          {/* Условная разбивка граф формы 3 — чтобы штамп читался как штамп. */}
          <line
            className="stamp-line"
            x1={stamp.x + 65}
            y1={H - stamp.y - stamp.h}
            x2={stamp.x + 65}
            y2={H - stamp.y}
          />
          <line
            className="stamp-line"
            x1={stamp.x + 65}
            y1={H - stamp.y - 40}
            x2={stamp.x + stamp.w}
            y2={H - stamp.y - 40}
          />
          <line
            className="stamp-line"
            x1={stamp.x + 135}
            y1={H - stamp.y - 40}
            x2={stamp.x + 135}
            y2={H - stamp.y}
          />
          <clipPath id={`${clipBase}-stamp`}>
            <rect x={stamp.x + 65} y={H - stamp.y - stamp.h} width={stamp.w - 65} height={15} />
          </clipPath>
          <text
            className="stamp-text"
            x={stamp.x + 68}
            y={H - stamp.y - stamp.h + 10}
            fontSize={smallSize}
            clipPath={`url(#${clipBase}-stamp)`}
          >
            Основная надпись, форма 3
          </text>
        </g>

        {ZONE_KEYS.map((key) => {
          const r = zoneRect(zones, key);
          if (!r) return null;
          const x = r.x;
          const y = H - r.y - r.h;
          const isSel = key === selected;
          const conflict = conflictKeys.has(key);
          const label = ZONE_LABELS[key];
          return (
            <Zone
              key={key}
              $selected={isSel}
              $conflict={conflict}
              $main={key === 'drawing'}
              $editable={editable}
              tabIndex={0}
              role="button"
              aria-pressed={isSel}
              aria-label={`${label}: ${Math.round(r.w)}×${Math.round(r.h)} мм, от левого края ${Math.round(r.x)} мм, от низа ${Math.round(r.y)} мм${conflict ? ', пересекается' : ''}`}
              onPointerDown={(e) => begin(e, key, 'move')}
              onKeyDown={(e) => keyboard(e, key)}
            >
              <rect
                className="focus"
                x={x - mm(4)}
                y={y - mm(4)}
                width={r.w + mm(8)}
                height={r.h + mm(8)}
              />
              <rect className="body" x={x} y={y} width={r.w} height={r.h} />
              <clipPath id={`${clipBase}-${key}`}>
                <rect x={x} y={y} width={r.w} height={r.h} />
              </clipPath>
              <text
                className="title"
                x={x + mm(8)}
                y={y + mm(8) + titleSize}
                fontSize={titleSize}
                clipPath={`url(#${clipBase}-${key})`}
              >
                {label}
              </text>
              <text
                className="size"
                x={x + mm(8)}
                y={y + mm(12) + titleSize + smallSize}
                fontSize={smallSize}
                clipPath={`url(#${clipBase}-${key})`}
              >
                {Math.round(r.w)}×{Math.round(r.h)} мм
              </text>
              {isSel &&
                editable &&
                HANDLES.map((h) => {
                  const hx = h.includes('w') ? x : h.includes('e') ? x + r.w : x + r.w / 2;
                  const hy = h.includes('n') ? y : h.includes('s') ? y + r.h : y + r.h / 2;
                  return (
                    <rect
                      key={h}
                      className={`handle h-${h}`}
                      x={hx - handle / 2}
                      y={hy - handle / 2}
                      width={handle}
                      height={handle}
                      onPointerDown={(e) => begin(e, key, h)}
                    />
                  );
                })}
            </Zone>
          );
        })}

        {overlaps.map((o, i) => (
          <rect key={i} className="overlap" x={o.x} y={H - o.y - o.h} width={o.w} height={o.h} />
        ))}
      </Svg>
    </Wrap>
  );
}
